import { normalizeAppLangCode } from '../core/i18n.js';
import { fetchLocalePack, getCachedLocalePack } from '../core/i18n-runtime.js';
import { ES_UI_BOOT_STUB, EN_UI_BOOT_STUB, bootStubForLang } from './shell-boot-stubs.js';
import { getPanelRef } from '../app/panel-refs.js';

/** @param {import('./shell-store.js').ShellStore} store */
export async function loadLanguageOnStore(store, langCode) {
    const lang = normalizeAppLangCode(langCode);
    const cached = getCachedLocalePack(lang);
    if (cached) {
        if (normalizeAppLangCode(store.state.lang) !== lang) return;
        store.update({ i18nData: cached });
        return;
    }
    const stub = lang === 'ES' ? ES_UI_BOOT_STUB : EN_UI_BOOT_STUB;
    if (!store.state.i18nData) {
        store.update({ i18nData: { ...stub } });
    }
    try {
        const boot = await fetchLocalePack(lang);
        if (normalizeAppLangCode(store.state.lang) !== lang) return;
        store.update({ i18nData: boot });
    } catch (e) {
        console.error(`Language load failed for ${lang}`, e);
        if (lang !== 'EN') await loadLanguageOnStore(store, 'EN');
        else {
            store.update({
                i18nData: {
                    appTitle: 'Arborito (Recovery)',
                    loading: 'Loading...',
                    errorTitle: 'Error',
                    errorNoTrees: 'Language Error',
                },
            });
        }
    }
}

/**
 * Change UI language and optionally reload the active tree.
 * Do not dismiss the language / onboarding modal before this promise settles.
 * @param {import('./shell-store.js').ShellStore} store
 * @returns {Promise<boolean>} true when language was applied; false if aborted (e.g. leave cancel)
 */
export async function setLanguageOnStore(store, lang, opts = {}) {
    const uiOnly = !!(opts && opts.uiOnly);
    const target = normalizeAppLangCode(lang);
    if (normalizeAppLangCode(store.state.lang) === target && store.state.i18nData) return true;

    const modalSnap = store.state.modal;
    const modalType = typeof modalSnap === 'string' ? modalSnap : modalSnap && modalSnap.type;
    const deferNavigation = modalType === 'language' || modalType === 'onboarding';

    if (uiOnly && modalType === 'onboarding') {
        const cached = getCachedLocalePack(target);
        store.update({
            lang: target,
            i18nData: cached || bootStubForLang(target),
            loading: false,
        });
        if (cached) return true;
        void fetchLocalePack(target)
            .then((pack) => {
                const stillOnboarding = (() => {
                    const m = store.state.modal;
                    return typeof m === 'object' && m && m.type === 'onboarding';
                })();
                if (!stillOnboarding) return;
                if (normalizeAppLangCode(store.state.lang) !== target) return;
                store.update({ i18nData: pack });
            })
            .catch(() => {});
        return true;
    }

    /* Ask before applying lang / reloading curriculum (incl. language modal). */
    if (!uiOnly) {
        try {
            const contentApi = getPanelRef('content');
            if (typeof contentApi?.confirmLeaveIfNeeded === 'function') {
                const ok = await contentApi.confirmLeaveIfNeeded();
                if (!ok) return false;
            }
        } catch {
            /* ignore */
        }
    }

    if (!uiOnly) {
        /* Skip loading chrome when the pack is already warm (common after prefetch). */
        if (!getCachedLocalePack(target)) {
            store.update({ loading: true, error: null });
        } else {
            store.update({ error: null });
        }
    }

    let appliedLang = target;
    /** @type {object | null} */
    let pack = null;
    try {
        pack = await fetchLocalePack(target);
    } catch (e) {
        console.error('[Arborito] setLanguage: pack load failed', target, e);
        if (target !== 'EN') {
            try {
                appliedLang = 'EN';
                pack = await fetchLocalePack('EN', { bypassCache: true });
            } catch (e2) {
                console.error('[Arborito] setLanguage: EN fallback failed', e2);
                store.update({ loading: false, error: String((e && e.message) || e) });
                return false;
            }
        } else {
            store.update({ loading: false, error: String((e && e.message) || e) });
            return false;
        }
    }

    store.update({
        lang: appliedLang,
        i18nData: pack,
        searchCache: {},
        ...(uiOnly ? { loading: false } : {}),
    });

    if (uiOnly) return true;

    try {
        if (store.state.activeSource) await store.loadData(store.state.activeSource, false);
        else store.update({ loading: false });
    } catch (e) {
        console.error('[Arborito] setLanguage: curriculum reload failed', e);
        store.update({ loading: false, error: String((e && e.message) || e) });
        return false;
    }

    if (!deferNavigation) {
        store.goHome();
    }
    return true;
}
